import { focusSelector, isVisible, focusableElements, tabBoundary, isolateOutside, focusColorForBackground } from '../utils/keyboard-focus.utils.js';

const initKeyboardAccessibility = () => {
    if (document.documentElement.dataset.keyboardAccessibility === 'ready') return;
    document.documentElement.dataset.keyboardAccessibility = 'ready';

    const popup = document.getElementById('popup');
    const header = document.querySelector('.header');
    const navigation = header?.querySelector('.navbar-nav');
    const burger = header?.querySelector('.burger-btn');
    let scope = null;
    let trigger = null;
    let restoreBackground = () => {};
    let queued = false;
    let lastInteraction = null;
    const swipers = new WeakSet();
    const carouselRegions = new Map();
    const returnTargets = new WeakMap();
    const lastFocused = new WeakMap();
    const cookieRoots = new WeakSet();

    const mobileMenu = () => burger && getComputedStyle(burger).display !== 'none';
    const extraRoots = () => Array.from(document.querySelectorAll('.meta-select-list.is-open, .air-datepicker.-active-')).filter((element) => {
        if (!scope || !isVisible(element)) return false;
        if (scope.contains(element)) return true;
        if (element.matches('.meta-select-list')) {
            return Boolean(scope.querySelector(`[aria-controls="${CSS.escape(element.id)}"]`));
        }
        return Array.from(scope.querySelectorAll('[data-site-date-picker]')).some((input) => input._siteDatepicker?.$datepicker === element);
    });
    const roots = () => {
        if (!scope) return [];
        const backdrop = scope === header ? document.querySelector('.header-close-wrapper') : scope.closest('#popup')?.querySelector('.popup__close-bg');
        return [scope, ...extraRoots(), ...(backdrop ? [backdrop] : [])];
    };
    const insideScope = (element) => roots().some((root) => root.contains(element));
    const focusStart = () => {
        const target = (scope === header ? navigation?.querySelector('a[href]') : scope?.querySelector('.popup__title')) || focusableElements(roots())[0] || scope;
        if (!target) return;
        if (target.tabIndex < 0 && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
    };
    const restoreFocus = (target) => {
        if (target && target.matches(focusSelector) && isVisible(target) && !target.matches(':disabled')) {
            target.focus({ preventScroll: true });
            return;
        }
        const main = document.getElementById('content');
        if (main) {
            main.setAttribute('tabindex', '-1');
            main.focus({ preventScroll: true });
        }
    };

    const sync = () => {
        queued = false;
        restoreBackground();
        restoreBackground = () => {};
        const menuOpen = Boolean(header?.classList.contains('is-open-menu') && mobileMenu());
        if (navigation) {
            navigation.inert = Boolean(mobileMenu() && !menuOpen);
            navigation.querySelectorAll('.simplebar-content-wrapper[tabindex]').forEach((wrapper) => {
                if (wrapper.getAttribute('tabindex') !== '-1') wrapper.setAttribute('tabindex', '-1');
            });
        }
        if (burger) {
            burger.setAttribute('aria-expanded', String(menuOpen));
            burger.setAttribute('aria-label', menuOpen ? 'Close navigation' : 'Open navigation');
        }
        const dialog = popup?.classList.contains('active') ? popup.querySelector('.popup__block') : null;
        if (dialog) {
            dialog.setAttribute('role', 'dialog');
            dialog.setAttribute('aria-modal', 'true');
            dialog.setAttribute('tabindex', '-1');
            const title = dialog.querySelector('.popup__title');
            if (title) {
                title.id ||= 'public-popup-title';
                dialog.setAttribute('aria-labelledby', title.id);
            } else dialog.setAttribute('aria-label', 'Dialog');
            const close = dialog.querySelector('.popup__btn-close');
            if (close && close.tagName !== 'BUTTON') {
                close.setAttribute('role', 'button');
                close.setAttribute('tabindex', '0');
                close.setAttribute('aria-label', 'Close dialog');
            }
        }
        document.querySelectorAll('.termsfeed-com---nb, .termsfeed-com---pc-overlay, .termsfeed-com---pc-dialog').forEach((root) => {
            if (cookieRoots.has(root)) return;
            cookieRoots.add(root);
            new MutationObserver(queueSync).observe(root, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        });
        const preferences = Array.from(document.querySelectorAll('.termsfeed-com---pc-dialog')).find(isVisible);
        const notice = Array.from(document.querySelectorAll('.termsfeed-com---nb')).find(isVisible);
        if (notice) {
            notice.setAttribute('role', 'dialog');
            notice.setAttribute('aria-modal', 'true');
            notice.setAttribute('aria-label', 'Cookie consent');
        }
        const nextScope = preferences || notice || dialog || (menuOpen ? header : null);
        if (nextScope !== scope) {
            const previousTrigger = trigger;
            const previousScope = scope;
            scope = nextScope;
            const replacingPopup = previousScope?.matches('.popup__block') && scope?.matches('.popup__block');
            const activeTrigger = lastInteraction?.isConnected ? lastInteraction : document.activeElement;
            trigger = scope ? (returnTargets.get(scope) || (replacingPopup ? previousTrigger : activeTrigger)) : null;
            if (scope) {
                returnTargets.set(scope, trigger);
                const resumeTarget = lastFocused.get(scope);
                if (resumeTarget && scope.contains(resumeTarget) && isVisible(resumeTarget)) restoreFocus(resumeTarget);
                else if (previousTrigger && scope.contains(previousTrigger)) restoreFocus(previousTrigger);
                else focusStart();
            } else if (previousScope) restoreFocus(previousTrigger);
            if (previousScope && !isVisible(previousScope)) returnTargets.delete(previousScope);
        }
        if (scope) {
            if (!insideScope(document.activeElement)) focusStart();
            restoreBackground = isolateOutside(roots());
        }
    };

    const queueSync = () => {
        if (queued) return;
        queued = true;
        queueMicrotask(sync);
    };

    const setFocusColor = (target) => {
        if (!(target instanceof HTMLElement)) return;
        let current = target.parentElement;
        let dark = false;
        let background = null;
        while (current) {
            if (current === document.body || current === document.documentElement) break;
            const color = getComputedStyle(current).backgroundColor.match(/[\d.]+/g)?.map(Number);
            if (color && (color.length < 4 || color[3] > 0.8)) {
                background = color.slice(0, 3);
                break;
            }
            current = current.parentElement;
        }
        if (!background) dark = Boolean(target.closest('.header')) || getComputedStyle(target).color.match(/\d+/g)?.slice(0, 3).map(Number).reduce((sum, value) => sum + value, 0) > 450;
        const owner = target.closest('.meta-checkbox, .cc-custom-checkbox, .meta-upload-file') || target;
        owner.style.setProperty('--site-focus-color', background ? focusColorForBackground(background) : (dark ? 'var(--site-focus-light)' : 'var(--site-focus-dark)'));
    };

    document.addEventListener('pointerdown', (event) => {
        lastInteraction = event.target instanceof Element ? event.target.closest(focusSelector) : null;
    }, true);
    document.addEventListener('keydown', (event) => {
        if (!(event.target instanceof Element)) return;
        if (['Enter', ' '].includes(event.key)) lastInteraction = document.activeElement;
        if (event.target.matches('.popup__btn-close[role="button"], .swiper-pagination-bullet[role="button"]:not(button)') && ['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            event.target.click();
            return;
        }
        if (!scope) return;
        if (event.key === 'Escape') {
            if (scope.querySelector('.meta-select-inner.is-open') || extraRoots().some((root) => root.matches('.air-datepicker'))) return;
            queueMicrotask(() => {
                if (event.defaultPrevented) return;
                if (scope?.matches('.termsfeed-com---pc-dialog')) scope.querySelector('.cc-pc-head-close')?.click();
                else if (scope?.matches('.termsfeed-com---nb')) return;
                else if (scope === header) burger?.click();
                else window.closePopup?.();
            });
        }
        if (event.key === 'Tab' && !event.defaultPrevented) {
            const elements = focusableElements(roots());
            const target = tabBoundary(elements, document.activeElement, event.shiftKey);
            if (target || !elements.length) {
                event.preventDefault();
                (target || scope).focus();
            }
        }
    });
    document.addEventListener('focusin', (event) => {
        setFocusColor(event.target);
        if (scope && !insideScope(event.target)) focusStart();
        if (scope?.contains(event.target)) lastFocused.set(scope, event.target);
        carouselRegions.forEach((region, instance) => {
            if (!instance.destroyed && region.contains(event.target)) instance.autoplay?.stop();
        });
        const slider = event.target.closest('.swiper');
        const instance = slider?.swiper;
        if (instance && !instance.destroyed) {
            instance.autoplay?.stop();
            const slide = event.target.closest('.swiper-slide');
            const index = Array.from(instance.slides).indexOf(slide);
            if (index >= 0 && !slide.classList.contains('swiper-slide-duplicate')) {
                const bounds = slider.getBoundingClientRect();
                const box = slide.getBoundingClientRect();
                if (box.left < bounds.left || box.right > bounds.right) instance.slideTo(index, 0);
            }
        }
    });

    const initSliders = () => document.querySelectorAll('.swiper').forEach((element) => {
        const instance = element.swiper;
        if (!instance || instance.destroyed || swipers.has(instance)) return;
        swipers.add(instance);
        const focusRegion = element.closest('[class*="swiper-wr"]') || element;
        carouselRegions.set(instance, focusRegion);
        element.setAttribute('role', 'region');
        element.setAttribute('aria-roledescription', 'carousel');
        if (!element.hasAttribute('aria-label')) {
            element.setAttribute('aria-label', element.closest('section')?.querySelector('h2')?.textContent.trim() || 'Carousel');
        }
        const updateSlides = () => {
            Array.from(instance.pagination?.bullets || []).forEach((bullet, index) => {
                bullet.setAttribute('role', 'button');
                bullet.setAttribute('tabindex', '0');
                bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
                bullet.setAttribute('aria-current', String(bullet.classList.contains('swiper-pagination-bullet-active')));
            });
            const rect = element.getBoundingClientRect();
            Array.from(instance.slides).forEach((slide) => {
                const box = slide.getBoundingClientRect();
                const hidden = box.right <= rect.left + 1 || box.left >= rect.right - 1 || slide.classList.contains('swiper-slide-duplicate');
                const controlled = Boolean(instance.navigation?.nextEl || instance.pagination?.el);
                slide.inert = (slide.classList.contains('swiper-slide-duplicate') || (hidden && controlled)) && !slide.contains(document.activeElement);
            });
        };
        instance.on('slideChange slideChangeTransitionEnd update resize breakpoint', updateSlides);
        instance.on('destroy', () => {
            carouselRegions.delete(instance);
            Array.from(instance.slides || []).forEach((slide) => { slide.inert = false; });
        });
        focusRegion.addEventListener('focusout', () => queueMicrotask(() => {
            if (focusRegion.contains(document.activeElement) || instance.destroyed) return;
            const rect = element.getBoundingClientRect();
            const visibleHeight = Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0);
            if (rect.height > 0 && visibleHeight / rect.height >= 0.85) instance.autoplay?.start();
            updateSlides();
        }));
        updateSlides();
    });

    if (popup) new MutationObserver(queueSync).observe(popup, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    if (header) new MutationObserver(queueSync).observe(header, { attributes: true, attributeFilter: ['class'] });
    if (navigation) new MutationObserver(queueSync).observe(navigation, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex'] });
    const content = document.getElementById('content');
    if (content) new MutationObserver(() => { initSliders(); }).observe(content, { childList: true, subtree: true });
    document.addEventListener('click', () => { queueSync(); initSliders(); });
    window.addEventListener('resize', () => { queueSync(); initSliders(); });
    window.addEventListener('load', () => { queueSync(); initSliders(); });
    window.syncPublicKeyboardFocus = queueSync;
    sync();
    initSliders();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initKeyboardAccessibility, { once: true });
else initKeyboardAccessibility();
